import { useEffect, useRef } from "react"
import { useCookieConsent } from "@/context/cookie-consent-context"
import { ADS_CONFIG } from "@/config/ads"

interface GoogleAnalyticsProps {
  measurementId: string
}

// Global tracking to prevent multiple initializations
const globalInitTracker = new Set<string>();
const globalScriptTracker = new Set<string>();

export function GoogleAnalytics({ measurementId }: GoogleAnalyticsProps) {
  const { preferences, hasConsented } = useCookieConsent()
  const isInitialized = useRef(false)
  const scriptLoaded = useRef(false)
  const mountedRef = useRef(false)

  // Only load script if analytics consent is granted
  const shouldLoadScript = hasConsented && preferences.analytics

  // Script loading effect - only when analytics consent is granted
  useEffect(() => {
    if (!ADS_CONFIG.ENABLE_ANALYTICS || !measurementId || !shouldLoadScript) return

    // Prevent double execution in StrictMode
    if (mountedRef.current) return
    mountedRef.current = true

    const scriptId = `ga-script-${measurementId}`

    // Check if script already loaded globally
    if (globalScriptTracker.has(measurementId)) {
      scriptLoaded.current = true
      return
    }

    const existingScript = document.getElementById(scriptId)
    if (existingScript) {
      scriptLoaded.current = true
      globalScriptTracker.add(measurementId)
      return
    }



    const script = document.createElement('script')
    script.id = scriptId
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
    script.setAttribute('data-measurement-id', measurementId)

    script.onload = () => {

      scriptLoaded.current = true
      globalScriptTracker.add(measurementId)
    }

    script.onerror = () => {
      console.error('📊 Failed to load Google Analytics script')
      scriptLoaded.current = false
    }

    try {
      document.head.appendChild(script)
    } catch (error) {
      console.error('📊 Error appending GA script:', error)
    }

    // Initialize gtag function if not exists (this should already be done by consent context)
    if (!window.dataLayer) {
      window.dataLayer = []
    }
    if (!window.gtag) {
      window.gtag = function gtag() {
        window.dataLayer!.push(arguments)
      }
    }

    return () => {
      // Don't remove script in cleanup to avoid StrictMode issues
      // Script persistence is intentional for GA
    }
  }, [measurementId, shouldLoadScript])

  // Initialization effect - only when consent is granted
  useEffect(() => {
    if (!hasConsented || !ADS_CONFIG.ENABLE_ANALYTICS || !measurementId || !preferences.analytics) {
      return
    }

    // Check if already initialized globally
    if (globalInitTracker.has(measurementId)) {
      isInitialized.current = true
      return
    }

    const initializeGA = () => {
      if (!window.gtag) {
        console.warn('📊 gtag not available for initialization')
        return
      }

      try {


        // Configure GA with consent mode aware settings
        window.gtag('config', measurementId, {
          page_title: document.title,
          page_location: window.location.href,
          send_page_view: true,
          // Respect consent mode
          analytics_storage: preferences.analytics ? 'granted' : 'denied',
          ad_storage: preferences.advertising ? 'granted' : 'denied',
          // Enhanced privacy settings
          anonymize_ip: true,
          allow_google_signals: preferences.advertising,
          cookie_flags: 'SameSite=None;Secure'
        })

        globalInitTracker.add(measurementId)
        isInitialized.current = true

      } catch (error) {
        console.error('📊 Error initializing Google Analytics:', error)
      }
    }

    // Wait for script to load with robust checking
    if (scriptLoaded.current && window.gtag) {
      // Small delay to ensure everything is ready
      setTimeout(initializeGA, 100)
    } else {
      let attempts = 0
      const maxAttempts = 50 // 5 seconds max

      const checkScript = setInterval(() => {
        attempts++

        if (window.gtag && (scriptLoaded.current || globalScriptTracker.has(measurementId))) {
          clearInterval(checkScript)
          setTimeout(initializeGA, 100)
        } else if (attempts >= maxAttempts) {
          clearInterval(checkScript)
          console.warn('📊 Timeout waiting for Google Analytics script')
        }
      }, 100)

      return () => clearInterval(checkScript)
    }
  }, [hasConsented, measurementId, preferences.analytics, preferences.advertising])

  // Handle consent withdrawals - disable tracking if consent is withdrawn
  useEffect(() => {
    if (hasConsented && window.gtag && isInitialized.current) {
      if (!preferences.analytics) {
        // Disable analytics tracking
        window.gtag('config', measurementId, {
          send_page_view: false,
          analytics_storage: 'denied'
        })

      }
    }
  }, [preferences.analytics, hasConsented, measurementId])

  return null
}

// Helper function to track events (only if consent is granted)
export const trackEvent = (action: string, category: string, label?: string, value?: number) => {
  if (window.gtag && typeof window !== 'undefined') {
    // Check if analytics consent is granted
    const cookieConsent = localStorage.getItem('cookie-consent')
    if (cookieConsent) {
      try {
        const { preferences } = JSON.parse(cookieConsent)
        if (preferences.analytics) {
          window.gtag('event', action, {
            event_category: category,
            event_label: label,
            value: value
          })
        }
      } catch (error) {
        console.warn('📊 Could not track event due to consent check error:', error)
      }
    }
  }
}

// Helper function to track page views (only if consent is granted)
export const trackPageView = (url: string, title?: string) => {
  if (window.gtag && typeof window !== 'undefined') {
    // Check if analytics consent is granted
    const cookieConsent = localStorage.getItem('cookie-consent')
    if (cookieConsent) {
      try {
        const { preferences } = JSON.parse(cookieConsent)
        if (preferences.analytics) {
          window.gtag('config', 'GA_MEASUREMENT_ID', {
            page_path: url,
            page_title: title
          })
        }
      } catch (error) {
        console.warn('📊 Could not track page view due to consent check error:', error)
      }
    }
  }
} 